/**
 * What the scan did not look at (UI_SDD §R.4c, DEC-146).
 *
 * Since DEC-189 this is the "Not examined" box in the "What TDIA checked" tab, beside
 * "Could not run", rather than a card in the Findings page's left column.
 *
 * The report answered "what is wrong" and never "what did you look at", so an empty
 * section read as a clean result. On a repository in a language TDIA cannot analyse, that
 * produced a near-empty report that looked like a clean bill of health.
 *
 * Two rules govern the styling, both from DEC-146:
 *
 *   1. **This is not a finding.** It takes no severity colour. A skipped agent is usually
 *      a correct decision ("no package manifest, so dependency analysis did not run"),
 *      and painting it red would be as wrong as hiding it.
 *   2. **Every line explains itself.** A list of agent ids with no reasons would move the
 *      silence up one level rather than removing it.
 *   3. **Ran-in-part is its own row (DEC-151).** An agent that ran without a tool it needed
 *      is neither a skipped agent nor a clean one, and folding it into either loses the
 *      distinction. Until migration 030 these warnings existed only in a live event and
 *      were gone on reload, which is the same silence in a different place.
 */
import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import type { NotExaminedSummary } from '../../types/api'
import { AGENT_LABELS } from '../../types/api'

interface NotExaminedCardProps {
  notExamined: NotExaminedSummary
}

function agentName(agentId: string): string {
  return AGENT_LABELS[agentId] ?? agentId
}

export function NotExaminedCard({ notExamined }: NotExaminedCardProps) {
  const {
    skipped_agents: agents,
    skipped_files: files,
    unanalyzed_languages: languages,
    partial_analyses: partial,
    coverage_not_recorded: notRecorded,
    limits,
  } = notExamined

  const nothingToSay =
    !notRecorded &&
    agents.length === 0 &&
    files.length === 0 &&
    languages.length === 0 &&
    partial.length === 0

  // Closed by default (founder, 2026-10-12: open, it pushed the checks far down). The closed
  // header still says what is inside, in counts, so nothing is hidden silently (§0.6).
  const [open, setOpen] = useState(false)
  const parts = [
    agents.length ? `${agents.length} ${agents.length === 1 ? 'agent' : 'agents'} did not run` : null,
    partial.length ? `${partial.length} ${partial.length === 1 ? 'analysis' : 'analyses'} ran only in part` : null,
    files.length ? `files skipped by ${files.length} ${files.length === 1 ? 'agent' : 'agents'}` : null,
    languages.length ? `${languages.length} ${languages.length === 1 ? 'language' : 'languages'} with no analyzer` : null,
  ].filter(Boolean)
  const canOpen = !notRecorded && !nothingToSay

  return (
    <section
      aria-label="Not examined"
      className="rounded-lg bg-fill-subtle border border-hairline-strong px-4 py-3 flex flex-col gap-[6px]"
    >
      {canOpen ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex items-start justify-between gap-3 text-left w-full"
        >
          <span className="flex flex-col gap-[2px] min-w-0">
            <span className="text-[13px] font-semibold text-accent-text">Not examined</span>
            <span className="text-[13px] text-muted leading-[1.45]" data-testid="not-examined-summary">
              {parts.join(' · ')}
            </span>
          </span>
          <ChevronDown
            aria-hidden
            className={`w-4 h-4 mt-[2px] flex-none text-faint transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>
      ) : (
        <h3 className="text-[13px] font-semibold text-accent-text">Not examined</h3>
      )}

      {notRecorded && (
        // Never "nothing was skipped": this scan ran before coverage was recorded, and
        // historical rows are deliberately not back-filled.
        <p className="text-[13px] text-muted leading-[1.5]">
          Coverage was not recorded for this scan. It ran before TDIA began tracking what
          it did not examine, so this section cannot say what was skipped.
        </p>
      )}

      {nothingToSay && (
        <p className="text-[13px] text-muted leading-[1.5]">
          Every agent ran in full, and every file in a supported language was read.
        </p>
      )}

      {canOpen && open && (
        <div className="flex flex-col gap-[6px] mt-[6px]" data-testid="not-examined-detail">
      {languages.length > 0 && (
        <div className="mb-[8px]">
          <div className="text-[13px] font-medium text-body mb-[6px]">
            Languages with no analyzer
          </div>
          <ul className="flex flex-col gap-[4px]">
            {languages.map((l) => (
              <li key={l.language}>
                <div className="flex items-baseline gap-[8px] text-[13px]">
                  <span className="text-body capitalize">{l.language}</span>
                  <span className="tabular-nums text-faint">
                    {l.file_count} {l.file_count === 1 ? 'file' : 'files'}
                  </span>
                  {/* DEC-147: a committed language is a stated promise, an acknowledged
                      one is an honest no. Collapsing them would tell the reader less
                      than TDIA knows. Status colour, never a severity colour: this is
                      not a finding. */}
                  <span
                    className={`text-[10px] uppercase tracking-[1px] font-semibold ${
                      l.tier === 'committed' ? 'text-warn' : 'text-faint'
                    }`}
                  >
                    {l.tier === 'committed' ? 'Committed' : 'Not supported'}
                  </span>
                </div>
                <div className="text-[12px] text-faint leading-[1.45]">{l.tier_meaning}</div>
              </li>
            ))}
          </ul>
          <p className="text-[12px] text-faint leading-[1.5] mt-[6px]">
            No findings were produced for these files. That is not the same as finding
            nothing.
          </p>
        </div>
      )}

      {partial.length > 0 && (
        <div className="mb-[8px]">
          <div className="text-[13px] font-medium text-body mb-[6px]">
            Analyses that ran only in part
          </div>
          <ul className="flex flex-col gap-[7px]">
            {partial.map((p, i) => (
              // Keyed by index as well as agent: one agent can warn more than once, and
              // DEC-151 stores every warning rather than only the first.
              <li key={`${p.agent_id}-${i}`}>
                <div className="text-[13px] text-body">{agentName(p.agent_id)}</div>
                <div className="text-[12px] text-faint leading-[1.45]">{p.warning}</div>
              </li>
            ))}
          </ul>
          <p className="text-[12px] text-faint leading-[1.5] mt-[6px]">
            These agents ran and completed. They saw less than the whole picture, so their
            findings are not a full account of what they cover.
          </p>
        </div>
      )}

      {agents.length > 0 && (
        <div className="mb-[8px]">
          <div className="text-[13px] font-medium text-body mb-[6px]">
            Analyses that did not run
          </div>
          <ul className="flex flex-col gap-[7px]">
            {agents.map((a) => (
              <li key={a.agent_id}>
                <div className="text-[13px] text-body">{agentName(a.agent_id)}</div>
                <div className="text-[12px] text-faint leading-[1.45]">{a.reason}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {files.length > 0 && (
        <div className="mb-[8px]">
          <div className="text-[13px] font-medium text-body mb-[6px]">Files not read</div>
          <ul className="flex flex-col gap-[7px]">
            {files.map((f) => (
              <li key={f.agent_id}>
                <div className="flex items-baseline gap-[8px]">
                  <span className="text-[13px] text-body flex-1 truncate">
                    {agentName(f.agent_id)}
                  </span>
                  <span className="tabular-nums text-[13px] text-faint flex-none">
                    {f.file_count}
                  </span>
                </div>
                <div className="text-[12px] text-faint leading-[1.45]">{f.reason}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!notRecorded && (
        // Stated, not hidden. A coverage section that looks complete while omitting a
        // category is the failure it exists to fix.
        <p className="text-[12px] text-faint leading-[1.5] pt-[8px] border-t border-hairline">
          {limits}
        </p>
      )}
        </div>
      )}
    </section>
  )
}
